/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

// Lib
import { apiClient } from '@lib/api-client';
import { TMutationConfig } from '@lib/react-query';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import { TApiResponseUpdateEmail } from '@features/auth/types';
import { TApiError } from '@global-types/api/api';
import { useNavigate } from 'react-router';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type UpdateEmailInput = {
	id: string | undefined;
	email: string;
};
/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const updateEmail = ({
	data,
}: {
	data: UpdateEmailInput;
}): Promise<TApiResponseUpdateEmail> => {
	return apiClient.post('/users/update_email', data);
};

type TUseUpdateEmailOptions = {
	mutationConfig?: TMutationConfig<typeof updateEmail>;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useUpdateEmail = ({
	mutationConfig,
}: TUseUpdateEmailOptions) => {
	const navigate = useNavigate();

	const { t } = useTranslation();

	const { onSuccess } = mutationConfig || {};

	return useMutation({
		mutationFn: updateEmail,
		onSuccess: (...args) => {
			onSuccess?.(...args);
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
			localStorage.removeItem('user');

			navigate('/');
		},
	});
};
